import React, { useEffect, useState } from 'react';
import { Loader2, Users, Clock } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import CreditHistoryTable from './CreditHistoryTable';

interface Therapist {
  id: string;
  name: string;
  email?: string;
}

const AdminHoursUsagePanel: React.FC = () => {
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [isLoadingTherapists, setIsLoadingTherapists] = useState(true);
  const [therapistsError, setTherapistsError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    const loadTherapists = async () => {
      try {
        setIsLoadingTherapists(true);
        setTherapistsError(null);
        const callable = httpsCallable<unknown, { therapists: Therapist[] }>(
          functions,
          'listTherapistsForHistory'
        );
        const result = await callable({});
        setTherapists(result.data.therapists || []);
      } catch (err: any) {
        console.error('Erro ao carregar terapeutas:', err);
        setTherapistsError(err?.message || 'Não foi possível carregar a lista de terapeutas');
      } finally {
        setIsLoadingTherapists(false);
      }
    };
    loadTherapists();
  }, []);

  const filteredTherapists = therapists.filter((t) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      (t.email && t.email.toLowerCase().includes(q))
    );
  });

  const selectedTherapist = therapists.find((t) => t.id === selectedUserId);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          Uso de Horas
        </h2>
        <p className="text-sm text-gray-500">
          Consulte o histórico completo de horas e sessões de cada terapeuta. O tempo em sala é exibido{' '}
          <strong>agrupado por reunião</strong> (uma linha por sessão, com total de horas correto), em vez de dezenas de
          lançamentos de 1 minuto.
        </p>
      </div>

      {/* Seleção do terapeuta */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-gray-500" />
          <h3 className="font-medium text-gray-900">Selecione um terapeuta</h3>
        </div>

        {isLoadingTherapists ? (
          <div className="flex items-center gap-2 text-gray-500 py-4">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Carregando terapeutas...</span>
          </div>
        ) : therapistsError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {therapistsError}
          </div>
        ) : (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Buscar por nome ou e-mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Escolha um terapeuta</option>
              {filteredTherapists.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.email ? ` (${t.email})` : ''}
                </option>
              ))}
            </select>
            {searchTerm && (
              <p className="text-xs text-gray-500">
                {filteredTherapists.length} terapeuta(s) encontrado(s)
              </p>
            )}
          </div>
        )}
      </div>

      {/* Histórico do terapeuta selecionado */}
      {selectedUserId ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3 pb-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">
              {selectedTherapist?.name || 'Terapeuta'}
            </h3>
            {selectedTherapist?.email && (
              <p className="text-xs text-gray-500 mt-0.5">{selectedTherapist.email}</p>
            )}
          </div>
          <CreditHistoryTable
            targetUserId={selectedUserId}
            showOwnerColumn={false}
          />
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
          Escolha um terapeuta acima para ver seu extrato completo de horas e sessões.
        </div>
      )}
    </div>
  );
};

export default AdminHoursUsagePanel;
